<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Estatus del campo</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" rounded elevation="2" @click="openCreateDialog()">
            <v-icon left>mdi-plus</v-icon>
            Nueva Salida
          </v-btn>
        </v-card-title>

        <!-- Tabs -->
        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-calendar</v-icon>
            Calendario
          </v-tab>
          <v-tab>
            <v-icon left>mdi-golf-tee</v-icon>
            Por Hoyo
          </v-tab>
          <v-tab>
            <v-icon left>mdi-table</v-icon>
            Tabla Detallada
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Calendario -->
          <v-tab-item>
            <v-card class="mb-4">
              <v-card-text class="pa-4">
                <v-row class="mb-4">
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="text-center pa-3">
                      <div class="display-1 font-weight-bold primary--text">{{ totalOutings }}</div>
                      <div class="caption grey--text">Salidas Totales</div>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="text-center pa-3">
                      <div class="display-1 font-weight-bold green--text">{{ completedOutings }}</div>
                      <div class="caption grey--text">Completadas</div>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="text-center pa-3">
                      <div class="display-1 font-weight-bold orange--text">{{ inProgressOutings }}</div>
                      <div class="caption grey--text">En Juego</div>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="text-center pa-3">
                      <div class="display-1 font-weight-bold info--text">{{ pendingOutings }}</div>
                      <div class="caption grey--text">Por Salir</div>
                    </v-card>
                  </v-col>
                </v-row>

                <v-toolbar flat color="white" class="mb-4">
                  <v-btn outlined class="mr-4" color="grey darken-2" @click="setToday">
                    Hoy
                  </v-btn>
                  <v-btn fab text small color="grey darken-2" @click="prev">
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn fab text small color="grey darken-2" @click="next">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-toolbar-title v-if="$refs.calendar">
                    {{ $refs.calendar.title }}
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                </v-toolbar>

                <v-sheet height="800">
                  <v-calendar
                    ref="calendar"
                    class="marshall-calendar"
                    v-model="focus"
                    color="primary"
                    type="month"
                    locale="es-mx"
                    @click:date="date => selectDate(date)">
                    <template v-slot:day="{ date }">
                      <div
                        class="d-flex justify-center align-center fill-height"
                        style="width: 100%; height: 100%;"
                        v-ripple
                        @click="selectDate(date)">
                        <v-chip
                          v-if="getOutingsForDate(date).length > 0"
                          color="primary"
                          small
                          class="font-weight-bold">
                          {{ getOutingsForDate(date).length }}
                        </v-chip>
                      </div>
                    </template>
                  </v-calendar>
                </v-sheet>

                <!-- Diálogo de salidas del día seleccionado -->
                <v-dialog v-model="dayDialog" max-width="800px">
                  <v-card>
                    <v-card-title>
                      <v-icon small class="mr-2">mdi-calendar-check</v-icon>
                      Salidas para {{ formatDate(selectedDate) }}
                      <v-spacer></v-spacer>
                      <v-btn icon @click="dayDialog = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-data-table
                        v-if="selectedDateOutings.length > 0"
                        :headers="dailyHeaders"
                        :items="selectedDateOutings"
                        class="elevation-0"
                        hide-default-footer>
                        <template v-slot:item.group="{ item }">
                          <v-chip small color="primary" text-color="white">Grupo {{ item.group_number }}</v-chip>
                        </template>
                        <template v-slot:item.players="{ item }">
                          <div>
                            <v-chip v-for="(player, index) in item.players" :key="index" x-small class="mr-1">
                              {{ player }}
                            </v-chip>
                          </div>
                        </template>
                        <template v-slot:item.status="{ item }">
                          <v-chip small :color="getStatusColor(item.status)" text-color="white">
                            {{ getStatusLabel(item.status) }}
                          </v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn small color="primary" text @click="openMarkDialog(item)">
                            Marcar Hoyo
                          </v-btn>
                        </template>
                      </v-data-table>
                      <div v-else class="text-center pa-6 grey--text">
                        Sin salidas para este día
                      </div>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Por Hoyo -->
          <v-tab-item>
            <v-card>
              <v-card-subtitle class="pa-4">
                <v-icon small class="mr-2">mdi-golf-tee</v-icon>
                Orden por Hoyo - {{ todayDate }}
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col v-for="hole in 18" :key="hole" cols="12" sm="6" md="4" lg="3">
                    <v-card outlined class="pa-4">
                      <div class="subtitle-2 font-weight-bold mb-3">Hoyo {{ hole }}</div>
                      <div v-if="getGroupsAtHole(hole).length > 0">
                        <v-chip
                          v-for="group in getGroupsAtHole(hole)"
                          :key="group.id"
                          small
                          color="info"
                          text-color="white"
                          class="mb-2 mr-1">
                          Grupo {{ group.group_number }}
                        </v-chip>
                      </div>
                      <div v-else class="caption grey--text">
                        Sin grupos
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Tabla Detallada -->
          <v-tab-item>
            <v-card class="mb-4">
              <v-card-subtitle class="pa-4">
                <v-icon small class="mr-2">mdi-table</v-icon>
                Detalle de Salidas
              </v-card-subtitle>
              <v-data-table
                :headers="headers"
                :items="todaysOutings"
                class="elevation-0"
                :no-data-text="'Sin salidas hoy'">

                <template v-slot:item.group_number="{ item }">
                  <v-chip color="primary" text-color="white" small>
                    Grupo {{ item.group_number }}
                  </v-chip>
                </template>

                <template v-slot:item.players="{ item }">
                  <div>
                    <v-chip v-for="(player, index) in item.players" :key="index" x-small class="mr-1 mb-1">
                      {{ player }}
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.scheduled_time="{ item }">
                  <div class="font-weight-bold">{{ item.scheduled_time }}</div>
                  <div v-if="item.actual_start_time" class="caption grey--text">
                    Inicio: {{ item.actual_start_time }}
                  </div>
                </template>

                <template v-slot:item.current_hole="{ item }">
                  <div class="text-center">
                    <v-chip color="info" text-color="white" small>
                      Hoyo {{ item.current_hole }}
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.pace_status="{ item }">
                  <v-chip :color="getPaceStatusColor(item.pace_status)" small>
                    {{ getPaceStatusText(item.pace_status) }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="openMarkDialog(item)">
                        <v-icon small color="orange">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Marcar hoyo</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="openWarningDialog(item)">
                        <v-icon small color="red">mdi-alert-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Registrar amonestación</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Diálogo para crear salida -->
    <v-dialog v-model="createDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>Nueva Salida</v-card-title>
        <v-card-text>
          <v-form ref="createForm" v-model="validCreate" lazy-validation>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newOuting.scheduled_time"
                  label="Hora de Salida"
                  type="time"
                  outlined
                  dense
                  :rules="[v => !!v || 'Hora requerida']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="newOuting.group_number"
                  label="Número de Grupo"
                  type="number"
                  outlined
                  dense
                  :rules="[v => v > 0 || 'Grupo requerido']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="newOutingPlayers"
                  label="Jugadores (separados por comas)"
                  outlined
                  dense
                  placeholder="Juan, Carlos, Roberto"
                  :rules="[v => !!v || 'Jugadores requeridos']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="createOuting" :disabled="!validCreate">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para marcar hoyo -->
    <v-dialog v-model="markDialog" max-width="500px" persistent>
      <v-card v-if="selectedOuting">
        <v-card-title>Marcar Hoyo - Grupo {{ selectedOuting.group_number }}</v-card-title>
        <v-card-text>
          <v-form ref="markForm" v-model="validMark" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="selectedOuting.current_hole"
                  label="Hoyo Actual"
                  type="number"
                  outlined
                  dense
                  min="1"
                  max="18"
                  :rules="[v => (v >= 1 && v <= 18) || 'Hoyo debe estar entre 1 y 18']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="selectedOuting.pace_status"
                  :items="[
                    { text: 'En Ritmo', value: 'on_pace' },
                    { text: 'Ligeramente Atrasado', value: 'slightly_behind' },
                    { text: 'Muy Atrasado', value: 'behind_schedule' }
                  ]"
                  label="Estado de Ritmo"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="markDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="updateOutingHole" :disabled="!validMark">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para advertencia (amonestación) -->
    <WarningDialog
      :dialog.sync="warningDialog"
      :group="selectedOuting"
      @save="recordWarning"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import WarningDialog from './components/WarningDialog.vue'

export default {
  name: 'MarshallOutings',
  components: {
    WarningDialog
  },
  data () {
    return {
      activeTab: 0,
      focus: '',
      selectedDate: new Date().toISOString().split('T')[0],
      createDialog: false,
      dayDialog: false,
      markDialog: false,
      warningDialog: false,
      validCreate: false,
      validMark: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      newOutingPlayers: '',
      selectedOuting: null,
      newOuting: {
        scheduled_time: '09:00',
        group_number: null
      },
      headers: [
        { text: 'Grupo', value: 'group_number', width: '10%' },
        { text: 'Jugadores', value: 'players', width: '30%', sortable: false },
        { text: 'Horario', value: 'scheduled_time', width: '15%' },
        { text: 'Hoyo', value: 'current_hole', width: '10%' },
        { text: 'Ritmo', value: 'pace_status', width: '15%' },
        { text: 'Acciones', value: 'actions', width: '20%', sortable: false, align: 'center' }
      ],
      dailyHeaders: [
        { text: 'Grupo', value: 'group', width: '15%' },
        { text: 'Jugadores', value: 'players', width: '40%', sortable: false },
        { text: 'Estado', value: 'status', width: '15%' },
        { text: 'Acciones', value: 'actions', width: '30%', sortable: false, align: 'center' }
      ],
      // MOCK DATA - Salidas
      outings: [
        {
          id: 1,
          group_number: 1,
          date: '2026-08-04',
          scheduled_time: '06:30',
          actual_start_time: '06:35',
          current_hole: 12,
          players: ['Carlos López', 'Juan García', 'Roberto Díaz'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 2,
          group_number: 2,
          date: '2026-08-04',
          scheduled_time: '06:40',
          actual_start_time: '06:42',
          current_hole: 10,
          players: ['Fernando Ruiz', 'Miguel Sánchez'],
          pace_status: 'behind_schedule',
          status: 'en_juego'
        },
        {
          id: 3,
          group_number: 3,
          date: '2026-08-04',
          scheduled_time: '06:50',
          actual_start_time: '06:50',
          current_hole: 6,
          players: ['Pedro Martínez', 'Alfonso Gómez', 'Luis Cortés'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 4,
          group_number: 4,
          date: '2026-08-04',
          scheduled_time: '07:00',
          actual_start_time: '07:05',
          current_hole: 8,
          players: ['Javier Torres', 'Enrique Vega'],
          pace_status: 'slightly_behind',
          status: 'en_juego'
        },
        {
          id: 5,
          group_number: 5,
          date: '2026-08-04',
          scheduled_time: '07:10',
          actual_start_time: '07:12',
          current_hole: 3,
          players: ['Raúl Soto', 'Arturo Mendez', 'Gilberto Núñez', 'Pablo Castro'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 6,
          group_number: 6,
          date: '2026-08-04',
          scheduled_time: '07:20',
          actual_start_time: null,
          current_hole: 0,
          players: ['David Vargas', 'Santiago Reyes'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 7,
          group_number: 7,
          date: '2026-08-04',
          scheduled_time: '07:30',
          actual_start_time: null,
          current_hole: 0,
          players: ['Mauricio Jiménez', 'Héctor Fuentes', 'Gustavo Morales'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 8,
          group_number: 8,
          date: '2026-08-04',
          scheduled_time: '07:40',
          actual_start_time: '07:42',
          current_hole: 1,
          players: ['Óscar Ramírez', 'Andrés Campos'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 9,
          group_number: 9,
          date: '2026-08-04',
          scheduled_time: '07:50',
          actual_start_time: null,
          current_hole: 0,
          players: ['Vicente López', 'Ramón García', 'Jesús Martínez'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 10,
          group_number: 10,
          date: '2026-08-04',
          scheduled_time: '08:00',
          actual_start_time: null,
          current_hole: 0,
          players: ['Emilio Sánchez', 'Cristóbal Vega'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 11,
          group_number: 11,
          date: '2026-08-04',
          scheduled_time: '08:10',
          actual_start_time: null,
          current_hole: 0,
          players: ['Julio Romero', 'Ignacio Flores', 'Ricardo Ortiz'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 12,
          group_number: 12,
          date: '2026-08-04',
          scheduled_time: '18:30',
          actual_start_time: '18:32',
          current_hole: 2,
          players: ['Álvaro Castro', 'Tomás Herrera'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 13,
          group_number: 13,
          date: '2026-08-05',
          scheduled_time: '06:30',
          actual_start_time: '06:30',
          current_hole: 18,
          players: ['Xavier Mendez', 'Sergio Peña', 'Valentín Ramos'],
          pace_status: 'on_pace',
          status: 'completado'
        },
        {
          id: 14,
          group_number: 14,
          date: '2026-08-05',
          scheduled_time: '06:40',
          actual_start_time: '06:45',
          current_hole: 15,
          players: ['Leopoldo Silva', 'Benito Aguirre'],
          pace_status: 'slightly_behind',
          status: 'en_juego'
        },
        {
          id: 15,
          group_number: 15,
          date: '2026-08-05',
          scheduled_time: '06:50',
          actual_start_time: '06:52',
          current_hole: 18,
          players: ['Marcelo Gómez', 'Vicente Castillo', 'Adrián Flores'],
          pace_status: 'on_pace',
          status: 'completado'
        },
        {
          id: 16,
          group_number: 16,
          date: '2026-08-05',
          scheduled_time: '07:00',
          actual_start_time: '07:08',
          current_hole: 14,
          players: ['Andrés Silva', 'Rodrigo Montoya'],
          pace_status: 'behind_schedule',
          status: 'en_juego'
        },
        {
          id: 17,
          group_number: 17,
          date: '2026-08-05',
          scheduled_time: '07:10',
          actual_start_time: null,
          current_hole: 0,
          players: ['Mateo Contreras', 'Nicolás Reyes', 'Simón Torres'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 18,
          group_number: 18,
          date: '2026-08-05',
          scheduled_time: '07:20',
          actual_start_time: null,
          current_hole: 0,
          players: ['Hugo Pacheco', 'Gerardo Rojas'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 19,
          group_number: 19,
          date: '2026-08-05',
          scheduled_time: '18:30',
          actual_start_time: '18:35',
          current_hole: 9,
          players: ['Oscar Martínez', 'Julio Navarro', 'Fernando Acosta'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 20,
          group_number: 20,
          date: '2026-08-05',
          scheduled_time: '18:40',
          actual_start_time: null,
          current_hole: 0,
          players: ['Esteban Vargas', 'Lorenzo Gutiérrez'],
          pace_status: 'on_pace',
          status: 'por_salir'
        }
      ]
    }
  },
  computed: {
    todayDate () {
      const today = new Date()
      return today.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    todaysOutings () {
      const today = new Date().toISOString().split('T')[0]
      return this.outings.filter(o => o.date === today)
    },
    selectedDateOutings () {
      return this.outings.filter(o => o.date === this.selectedDate)
    },
    totalOutings () {
      return this.outings.length
    },
    completedOutings () {
      return this.outings.filter(o => o.status === 'completado').length
    },
    inProgressOutings () {
      return this.outings.filter(o => o.status === 'en_juego').length
    },
    pendingOutings () {
      return this.outings.filter(o => o.status === 'por_salir').length
    }
  },
  methods: {
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    getOutingsForDate (date) {
      return this.outings.filter(o => o.date === date)
    },
    selectDate (date) {
      this.selectedDate = date
      this.dayDialog = true
    },
    getGroupsAtHole (hole) {
      return this.todaysOutings.filter(o => o.current_hole === hole)
    },
    getStatusColor (status) {
      const colors = {
        por_salir: 'grey',
        en_juego: 'blue',
        completado: 'green'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        por_salir: 'Por Salir',
        en_juego: 'En Juego',
        completado: 'Completado'
      }
      return labels[status] || 'Desconocido'
    },
    getPaceStatusColor (status) {
      const colors = {
        on_pace: 'green',
        slightly_behind: 'orange',
        behind_schedule: 'red'
      }
      return colors[status] || 'grey'
    },
    getPaceStatusText (status) {
      const texts = {
        on_pace: 'En Ritmo',
        slightly_behind: 'Ligeramente Atrasado',
        behind_schedule: 'Muy Atrasado'
      }
      return texts[status] || 'Desconocido'
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    setToday () {
      this.focus = ''
    },
    openCreateDialog () {
      this.newOuting = { scheduled_time: '09:00', group_number: null }
      this.newOutingPlayers = ''
      this.createDialog = true
    },
    createOuting () {
      if (this.$refs.createForm.validate()) {
        const newOuting = {
          id: Math.max(...this.outings.map(o => o.id), 0) + 1,
          ...this.newOuting,
          date: this.selectedDate,
          actual_start_time: null,
          current_hole: 0,
          players: this.newOutingPlayers.split(',').map(p => p.trim()),
          pace_status: 'on_pace',
          status: 'por_salir'
        }
        this.outings.push(newOuting)
        this.snackbarMessage = `Salida Grupo ${newOuting.group_number} creada`
        this.snackbarColor = 'success'
        this.snackbar = true
        this.createDialog = false
      }
    },
    openMarkDialog (outing) {
      this.selectedOuting = { ...outing }
      this.markDialog = true
    },
    updateOutingHole () {
      if (this.$refs.markForm.validate()) {
        const index = this.outings.findIndex(o => o.id === this.selectedOuting.id)
        if (index > -1) {
          this.outings[index].current_hole = this.selectedOuting.current_hole
          this.outings[index].pace_status = this.selectedOuting.pace_status
          if (!this.outings[index].actual_start_time) {
            this.outings[index].actual_start_time = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
          }
          this.outings[index].status = this.selectedOuting.current_hole >= 18 ? 'completado' : 'en_juego'
        }
        this.snackbarMessage = `Grupo ${this.selectedOuting.group_number} marcado en hoyo ${this.selectedOuting.current_hole}`
        this.snackbarColor = 'success'
        this.snackbar = true
        this.markDialog = false
      }
    },
    openWarningDialog (outing) {
      this.selectedOuting = outing
      this.warningDialog = true
    },
    recordWarning (warning) {
      this.snackbarMessage = `Amonestación registrada: ${warning.type}`
      this.snackbarColor = 'success'
      this.snackbar = true
      this.warningDialog = false
    }
  }
}
</script>

<style scoped>
::v-deep .marshall-calendar .v-calendar-weekly__day {
  min-height: 110px;
}
::v-deep .marshall-calendar .v-calendar-weekly__day-label {
  margin-bottom: 4px;
}
</style>
